
import { defineComponent, computed, watch, reactive } from 'vue'
import { AxiosResponse } from 'axios'
import JrSelect from '@/components/jr-parts/JrSelect.vue'
import { Department, User } from '@/api'
import { ListRequest } from '@/requests/user'

/**
 * 一覧を取得する
 */
const getUsers = async (sectionId?: number): Promise<User[]> => {
  const request = sectionId != null ? new ListRequest(sectionId) : new ListRequest()
  await request.exec()
  if (request.success) {
    const { data } = request.getResponse() as AxiosResponse<Array<User>>
    return data
  }
  return []
}

/**
 * ユーザーSelect
 */
export default defineComponent({
  name: 'UserSelect',
  components: {
    JrSelect,
  },
  emits: ['update:modelValue', 'update:name', 'update:option'],
  props: {
    // 社員コード
    modelValue: {
      type: String,
      required: false,
      default: null,
    },
    // 空白有無
    blank: {
      type: Boolean,
      required: false,
      default: true,
    },
    popperAppendToBody: {
      type: Boolean,
      required: false,
      default: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
    },
    isEdit: {
      type: Boolean,
      required: false,
      default: true,
    },
    nullable: {
      type: Boolean,
      required: false,
      default: false,
    },
    // 箇所に紐づけるかどうか
    filterBySection: {
      type: Boolean,
      required: false,
      default: false,
    },
    sectionId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  async setup(props, context) {
    const users = reactive<User[]>([])

    const filteredUser = computed(() => {
      // 箇所に紐づけない場合かつ箇所未指定時は全て、指定時はその箇所のユーザを抽出する。空白の選択肢はあれば必ず付与する
      return users.filter(
        (user) =>
          (!props.filterBySection && props.sectionId === null) ||
          user.section.section_id === props.sectionId ||
          user.shain_code === '' ||
          (user.shain_code === 'allUser' &&
            ((!props.filterBySection && props.sectionId === null) || props.sectionId != null))
      )
    })

    if (props.blank) {
      // 空白の選択肢を付与する
      users.push({
        section: {
          section_id: -1,
          section_code: '',
          section_name: '',
          section_sort: -1,
          is_jrqc: false,
          department: {} as Department,
        },
        shain_code: '',
        user_name: ' ',
        user_email: '',
        rank_type: -1,
        is_system_admin: false,
        is_jrqc_proximity: false,
        is_manager: false,
        is_jrqc_land_main: false,
      })
    }

    users.push({
      section: {
        section_id: -1,
        section_code: '',
        section_name: '',
        section_sort: -1,
        is_jrqc: false,
        department: {} as Department,
      },
      shain_code: 'allUser',
      user_name: '（全て）',
      user_email: '',
      rank_type: -1,
      is_system_admin: false,
      is_jrqc_proximity: false,
      is_manager: false,
      is_jrqc_land_main: false,
    })

    const update = (value: string) => {
      // 箇所に紐づけない場合かつ箇所未指定時は全て、指定時はその箇所のユーザを抽出
      const filteredUsers = users.filter(
        (user) => (!props.filterBySection && props.sectionId === null) || user.section.section_id === props.sectionId
      )
      // 選択したvalueを返す
      context.emit('update:modelValue', props.nullable && value === '' ? null : value, filteredUsers)
    }

    const updateName = (name: string | null) => {
      // 選択したnameを返す
      context.emit('update:name', name)
    }

    const updateOption = (user: User | null) => {
      // 選択したoptionを返す
      context.emit('update:option', user)
    }

    // 箇所変更watch
    watch(
      () => props.sectionId,
      async (newSectionId, oldSectionId) => {
        // 箇所が未指定ではなく、箇所との関連付けが有効の場合
        if (newSectionId != null && props.filterBySection) {
          // 既に対象箇所のユーザー情報があるか確認
          const index = users.findIndex((user) => user.section.section_id === newSectionId)
          if (index < 0) {
            users.push(...(await getUsers(newSectionId)))
          }
        }
        if (props.filterBySection && oldSectionId !== null) {
          // 前回の箇所が未指定から指定に変わった場合以外は選択肢を初期化する
          update('')
          updateName(null)
          updateOption(null)
        }
      }
    )

    if (!props.filterBySection) {
      // 箇所での絞り込み有効では無い場合、全取得する
      users.push(...(await getUsers()))
    } else if (props.sectionId != null) {
      // 初期で箇所が設定されている場合、取得する
      users.push(...(await getUsers(props.sectionId)))
    }

    return {
      filteredUser,
      update,
      updateName,
      updateOption,
    }
  },
})
